import {
  useEffect,
  useMemo,
  useState
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  Divider,
  alpha,
  IconButton,
  Tooltip
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useHistory } from 'react-router';
import { getSelectedSiteFilterValue, getStateName } from 'src/utils/getInitials';
import ROUTE from 'src/utils/routeConstants';
import Loader from 'src/common/Loader';
import { useDispatch, useSelector } from 'react-redux';
import { getOperatorList } from 'src/redux/operatorSlice';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { getAlignByColumnId, userTableHeaders } from 'src/utils/tableHeaders';
import CustomPagination from 'src/common/CustomPagination';
import {
  getTableActionIconButtonSx,
  TABLE_ACTION_ICON_SX
} from 'src/common/tableActionStyles';

const OperatorListTable = ({
  refreshData, searchText, siteFilter, ...rest
}) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();

  const { loading, operatorList, totalCount } = useSelector(
    (state) => state.operators
  );

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('first_name');
  const userAlignById = useMemo(() => getAlignByColumnId(userTableHeaders), []);
  const selectedSite = getSelectedSiteFilterValue(siteFilter);

  useEffect(() => {
    setPage(0);
    dispatch(
      getOperatorList({
        limit,
        pageNo: 1,
        params: { user__first_name: searchText, site: selectedSite }
      })
    );
  }, [dispatch, limit, refreshData, searchText, selectedSite]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(
      getOperatorList({
        limit,
        pageNo: newPage + 1,
        params: { user__first_name: searchText, site: selectedSite }
      })
    );
  };

  const viewOperatorInfo = (userId) => {
    history.push(`${ROUTE.operators}/${userId}`);
  };

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const displayData = operatorList
    ?.slice()
    .sort(getComparator(order, orderBy)) || [];

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Card
          sx={{
            ...rest?.sx,
            width: '100%',
            borderRadius: 2,
            bgcolor: '#FFFFFF',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              minHeight: 0,
              flex: 1,
              overflow: 'auto',
              '& .MuiTableCell-stickyHeader': {
                backgroundColor: '#F9FAFB',
                borderBottom: `1px solid ${theme.palette.divider}`,
                zIndex: 2
              },
              '& .MuiTableHead-root .MuiTableCell-root:last-of-type': {
                position: 'sticky',
                right: 0,
                zIndex: 3,
                backgroundColor: '#F9FAFB',
                boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
              }
            }}
          >
            <Box sx={{ minWidth: { xs: 760, sm: 900, md: 1050 } }}>
              <Table stickyHeader>
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  headCells={userTableHeaders}
                  onRequestSort={handleRequestSort}
                  sx={{
                    backgroundColor: '#F9FAFB',
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}
                />

                <TableBody>
                  {displayData.length > 0 ? (
                    displayData.map((user) => (
                      <TableRow
                        hover
                        key={user.id}
                        sx={{
                          borderBottom: `1px solid ${alpha(
                            theme.palette.divider,
                            0.5
                          )}`,
                          '&:last-child': { borderBottom: 'none' },
                          '&:hover': {
                            backgroundColor: '#F9FAFB'
                          }
                        }}
                      >
                        <TableCell align={userAlignById.first_name}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {`${user.first_name} ${user.last_name}`}
                          </Typography>
                        </TableCell>

                        <TableCell align={userAlignById.emp_id}>
                          <Typography variant="body2" color="text.secondary">
                            {user.emp_id}
                          </Typography>
                        </TableCell>

                        <TableCell align={userAlignById.mobile_num}>
                          <Typography variant="body2" color="text.secondary">
                            {user.mobile_num}
                          </Typography>
                        </TableCell>

                        <TableCell align={userAlignById.city}>
                          <Typography variant="body2" color="text.secondary">
                            {`${user.city}, ${getStateName(user.state)}`}
                          </Typography>
                        </TableCell>

                        <TableCell align={userAlignById.timezone}>
                          <Typography variant="body2" color="text.secondary">
                            {user.timezone || 'Asia/Kolkata'}
                          </Typography>
                        </TableCell>

                        <TableCell align={userAlignById.is_active}>
                          <Chip
                            label={user.is_active ? 'Active' : 'Inactive'}
                            color={user.is_active ? 'success' : 'error'}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>

                        <TableCell
                          align={userAlignById.action}
                          sx={{
                            position: 'sticky',
                            right: 0,
                            zIndex: 1,
                            backgroundColor: '#FFFFFF',
                            boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                          }}
                        >
                          <Tooltip title="View Details">
                            <IconButton
                              onClick={() => viewOperatorInfo(user.id)}
                              size="medium"
                              aria-label="View details"
                              sx={getTableActionIconButtonSx(theme)}
                            >
                              <ChevronRightIcon sx={TABLE_ACTION_ICON_SX} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={userTableHeaders.length}
                        align="center"
                        sx={{ py: 8 }}
                      >
                        <Typography color="text.secondary">
                          No Operators Found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>

          <Divider sx={{ flexShrink: 0 }} />

          <Box sx={{ p: 1, bgcolor: '#FFFFFF', flexShrink: 0 }}>
            <CustomPagination
              page={page}
              rowsPerPage={limit}
              count={totalCount}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleLimitChange}
            />
          </Box>
        </Card>
      )}
    </>
  );
};

OperatorListTable.propTypes = {
  refreshData: PropTypes.bool,
  searchText: PropTypes.string,
  siteFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default OperatorListTable;
